'use client'

import { createMultiStreamHook, createStreamHook } from '@/service/ws/use-web-socket-with-proto'
import {
  AccountFeesList,
  AccountSummaryList,
  PortfolioDataList,
} from '@/types/generated/common/account'
import { PositionList, TradeList } from '@/types/generated/common/trading'
import { ServiceNotification } from '@/types/generated/notifications'
import { AccountDataStream, Notifications, UnifiedOrderList } from '@/types/generated/public_api'

// Every account-data stream type shares the 'accountStream' channel, so a listener
// receives pushes for every subscribed account, not just its own. Most live frames
// are scoped to exactly one account on the envelope (`message.data.accountId`)
// regardless of how many accounts the subscribe-side channel covers. Matching here
// means a push for an account outside this payload's scope (or a legitimately-empty
// one) yields `undefined` and never reaches `onMessage`.
//
// The exception is a frame public-api coalesced across accounts: it carries rows
// for several accounts and blanks the envelope, because no single account owns it.
// That shape must go through `scopedMultiAccount`, which this function would
// otherwise reject outright — losing every account in the frame, not just the
// extra ones.
function matchesAccounts(
  messageAccountId: string | undefined,
  payload: AccountDataStream
): boolean {
  if (!messageAccountId) return false
  if (payload.accountIds.length > 0) return payload.accountIds.includes(messageAccountId)
  return messageAccountId === payload.accountId
}

function payloadDeps(p: AccountDataStream): unknown[] {
  // Sorted-joined so a scope that's unchanged in membership (just reordered)
  // doesn't spuriously look like a new payload and force a resubscribe.
  return [[...p.accountIds].sort().join(','), p.accountId, p.dataType]
}

/**
 * A batched-channel push, tagged with which account it belongs to — callers
 * need this to route the message to the right account's dump/delta state and
 * merge target, since one hook instance now covers every account in scope
 * instead of exactly one.
 */
export interface AccountScopedResponse<T> {
  accountId: string
  data: T
}

function scoped<T>(
  message: { data?: { accountId?: string } },
  payload: AccountDataStream,
  extract: () => T | undefined
): AccountScopedResponse<T> | undefined {
  const accountId = message.data?.accountId
  if (!matchesAccounts(accountId, payload) || !accountId) return undefined
  const data = extract()
  return data ? { accountId, data } : undefined
}

/**
 * `scoped`, extended to accept a frame whose envelope `accountId` is blank.
 *
 * public-api blanks the stamp when its outbound batcher coalesces several
 * accounts' rows into one frame — no single account owns that envelope. Only
 * safe for streams whose rows each carry their own `accountId` and whose
 * handler treats the envelope as a fallback (summary, fees). Requires a
 * multi-account subscription: a blank envelope on a single-account channel is
 * not a shape we emit, and there would be no id to fall back to.
 */
export function scopedMultiAccount<T>(
  message: { data?: { accountId?: string } },
  payload: AccountDataStream,
  extract: () => T | undefined
): AccountScopedResponse<T> | undefined {
  if (message.data?.accountId) return scoped(message, payload, extract)
  if (payload.accountIds.length === 0) return undefined
  const data = extract()
  return data ? { accountId: '', data } : undefined
}

export const usePositionsStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<PositionList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) => scoped(message, payload, () => message.data?.positions),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

export const useOpenOrdersStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<UnifiedOrderList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    scoped(message, payload, () => message.data?.unifiedOrders),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

export const useOrdersHistoryStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<TradeList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) => scoped(message, payload, () => message.data?.trades),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

// Balance is now part of PortfolioData
export const usePortfolioStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<PortfolioDataList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    scoped(message, payload, () => message.data?.portfolioData),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

// FEES: effective maker/taker fee schedule (latest-only, hourly).
export const useFeesStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<AccountFeesList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    scopedMultiAccount(message, payload, () => message.data?.accountFees),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

// SUMMARY: authoritative leverage / equity / PnL roll-up (latest-only snapshot).
export const useSummaryStream = createStreamHook<
  AccountDataStream,
  AccountScopedResponse<AccountSummaryList>
>({
  channelKey: 'accountStream',
  extractResponse: (message, payload) =>
    scopedMultiAccount(message, payload, () => message.data?.accountSummary),
  getPayloadDeps: payloadDeps,
  listenerChannelKey: 'accountStream',
})

// Service notifications (order filled, rejected, validation errors).
// Keep one listener while subscribing explicitly to every requested account. A
// notification is routed only when its accountId belongs to the current payload
// set; user-scoped notifications with no accountId (for example security alerts)
// are still allowed through once.
export const useMultiNotificationStream = createMultiStreamHook<Notifications, ServiceNotification>(
  {
    channelKey: 'serviceNotifications',
    extractResponse: (message, payloads) => {
      const notification = message.data?.notification
      if (!notification) return undefined
      if (!notification.accountId) return notification

      const subscribedAccountIds = new Set(
        payloads.flatMap((payload) =>
          payload.accountIds.length > 0
            ? payload.accountIds
            : payload.accountId
              ? [payload.accountId]
              : []
        )
      )
      return subscribedAccountIds.has(notification.accountId) ? notification : undefined
    },
    listenerChannelKey: 'serviceNotifications',
  }
)
